import React, { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { Tab } from "@headlessui/react";
import ModalIntelliPay from "./modal-intellipay";
import OffersStep from "./OffersStep";
import TshirtAndPositionStep from "../../../components/TshirtAndPositionStep";
import CombineBundleOffer from "../../../components/combine-bundle/CombineBundleOffer";
import {
  OPTION_BUNDLE,
  OPTION_SINGLE,
  OPTION_SINGLE_WITH_DRILL,
  formatMoney,
  getBundleId,
  getBundlePrice,
  getCombinePrice,
  getCombines,
  getDrillCombineCount,
  getHasPositionDrills,
  getPositionDrillPrice,
  getTotalSeparatePrice,
  isBundleOfferUsable,
  resolveSelectedCombine,
} from "../../../components/combine-bundle/bundleHelpers";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { useAxiosInstance } from "../../../services/Interceptor";

import { positions, tshirtSizes } from "../../../utils/PositionStateData";
import {
  getAthleteProfile,
  updateAthleteProfile,
} from "../../../services/AthleteService";
import { getCombineBundleForEvent } from "../../../services/EventsService";
import { toast, ToastContainer } from "react-toastify";

export default function RegisterFlowModal({
  open,
  onClose,
  athleteId,
  athleteEmail,
  eventId,
  origin,
  currentPosition,
}) {
  const apiInstance = useAxiosInstance();
  const trackEvent = useTrackEvent();

  const [activeTab, setActiveTab] = useState(0);
  const [showPayment, setShowPayment] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [bundleOption, setBundleOption] = useState(OPTION_SINGLE);
  // Handed to ModalIntelliPay; null means the athlete declined (or was never
  // offered) the bundle and pays for the single combine as before.
  const [bundleChoice, setBundleChoice] = useState(null);

  const [tshirtSize, setTshirtSize] = useState("");
  const [position, setPosition] = useState(currentPosition || "");
  const [originalPosition, setOriginalPosition] = useState(
    currentPosition || "",
  );
  const [saving, setSaving] = useState(false);
  const [offerAcceptanceStatus, setOfferAcceptanceStatus] = useState("no");
  const [offerAcceptanceSchool, setOfferAcceptanceSchool] = useState("");

  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setShowPayment(false);
      setBundle(null);
      setBundleOption(OPTION_SINGLE);
      setBundleChoice(null);
      setTshirtSize("");
      setOfferAcceptanceStatus("no");
      setOfferAcceptanceSchool("");
    }
  }, [open, currentPosition]);

  useEffect(() => {
    if (currentPosition) return;

    async function fetchData() {
      const response = await getAthleteProfile(apiInstance, athleteId);
      setPosition(response.primaryPosition);
      setOriginalPosition(response.primaryPosition);
    }
    fetchData();
  }, [apiInstance, athleteId, currentPosition]);

  const canContinueToPayment = !!tshirtSize && !!position;

  // The route answers 204 (empty body) when the combine is in no active
  // bundle or the athlete already owns a combine in it. That and any failure,
  // returned or thrown, mean no offer, so the athlete goes straight to payment
  // as before instead of being blocked by an optional upsell.
  const fetchBundleOffer = async () => {
    try {
      const response = await getCombineBundleForEvent(apiInstance, eventId);
      return response?.success ? response.data : null;
    } catch {
      return null;
    }
  };

  const saveAndContinueToPayment = async () => {
    if (!canContinueToPayment) return;

    setSaving(true);
    try {
      if (position && position !== originalPosition) {
        await updateAthleteProfile(apiInstance, athleteId, {
          primaryPosition: position,
        });
        toast.success("Position updated successfully");
      }
    } catch (err) {
      toast.error("Failed to update registration details. Please try again.");
      setSaving(false);
      throw err;
    }

    const offer = await fetchBundleOffer();
    setSaving(false);

    if (!isBundleOfferUsable(offer, eventId)) {
      setShowPayment(true);
      return;
    }

    const selectedCombine = resolveSelectedCombine(offer, eventId);
    setBundle(offer);
    setBundleOption(
      getHasPositionDrills(selectedCombine)
        ? OPTION_SINGLE_WITH_DRILL
        : OPTION_SINGLE,
    );
    trackEvent("Highschool_ViewedCombineBundleUpsell", {
      event_id: eventId,
      bundle_id: getBundleId(offer),
      bundle_price: getBundlePrice(offer),
      total_separate_price: getTotalSeparatePrice(offer),
      combine_count: getCombines(offer).length,
      combines_with_position_drills: getDrillCombineCount(offer),
      athlete_type: "returning",
      portal_type: "highschool",
    });
  };

  const continueFromBundleOffer = () => {
    if (bundleOption === OPTION_BUNDLE) {
      trackEvent("Highschool_AcceptedCombineBundle", {
        event_id: eventId,
        bundle_id: getBundleId(bundle),
        bundle_price: getBundlePrice(bundle),
        athlete_type: "returning",
        portal_type: "highschool",
      });
      setBundleChoice({
        bundleId: getBundleId(bundle),
        bundlePrice: getBundlePrice(bundle),
        combines: getCombines(bundle),
      });
    } else {
      trackEvent("Highschool_DeclinedCombineBundle", {
        event_id: eventId,
        bundle_id: getBundleId(bundle),
        position_drill: bundleOption === OPTION_SINGLE_WITH_DRILL,
        athlete_type: "returning",
        portal_type: "highschool",
      });
      setBundleChoice(null);
    }
    setShowPayment(true);
  };

  const showBundleOffer = !!bundle && !showPayment;

  const selectedBundleCombine = resolveSelectedCombine(bundle, eventId);
  const singlePrice = getCombinePrice(selectedBundleCombine);
  const bundleContinueLabel = {
    [OPTION_SINGLE_WITH_DRILL]: `Single Event + Position Drill – ${formatMoney(
      singlePrice + getPositionDrillPrice(selectedBundleCombine),
    )}`,
    [OPTION_SINGLE]: `Single Event Only – ${formatMoney(singlePrice)}`,
    [OPTION_BUNDLE]: `Continue with Bundle – ${formatMoney(getBundlePrice(bundle))}`,
  }[bundleOption];

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!showPayment) onClose();
      }}
      className="relative z-[250]"
    >
      <DialogBackdrop className="fixed inset-0 bg-black/50" />

      <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
        {showBundleOffer ? (
          <DialogPanel className="w-full max-w-6xl bg-white rounded-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  Combine Bundle
                </div>
                <div className="text-sm text-gray-600">
                  Save on more combines before you pay
                </div>
              </div>

              <button
                type="button"
                className="text-gray-500 hover:bg-gray-100 rounded-full p-2"
                onClick={onClose}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <CombineBundleOffer
                bundle={bundle}
                eventId={eventId}
                option={bundleOption}
                onOptionChange={setBundleOption}
                className="p-5"
              />
            </div>

            {/* Footer */}
            <div className="border-t px-5 py-4 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
              <button
                type="button"
                onClick={() => setBundle(null)}
                className="border-2 border-gray-700 text-gray-900 font-semibold rounded px-6 py-2.5 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={continueFromBundleOffer}
                className="bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded px-8 py-3"
              >
                {bundleContinueLabel}
              </button>
            </div>
          </DialogPanel>
        ) : !showPayment ? (
          <DialogPanel className="w-full max-w-5xl bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-gray-900">
                  Complete registration details
                </div>
                <div className="text-sm text-gray-600">
                  Step: {activeTab + 1} of 3
                </div>
              </div>

              <button
                type="button"
                className="text-gray-500 hover:bg-gray-100 rounded-full p-2"
                onClick={onClose}
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-5">
              <Tab.Group selectedIndex={activeTab} onChange={setActiveTab}>
                <Tab.List className="flex gap-2 border-b">
                  {["Offers", "T-Shirt Size", "Position"].map((label) => (
                    <Tab
                      key={label}
                      className={({ selected }) =>
                        `px-4 py-2 text-sm font-semibold -mb-[1px] border-b-2 ${
                          selected
                            ? "border-blue-600 text-blue-700"
                            : "border-transparent text-gray-600 hover:text-gray-900"
                        }`
                      }
                    >
                      {label}
                    </Tab>
                  ))}
                </Tab.List>

                <Tab.Panels className="mt-4">
                  <Tab.Panel unmount={false}>
                    <OffersStep
                      athleteId={athleteId}
                      onDone={(status, school) => {
                        setOfferAcceptanceStatus(status);
                        setOfferAcceptanceSchool(school);
                        setActiveTab(1);
                      }}
                    />
                  </Tab.Panel>

                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="tshirt"
                      tshirtSize={tshirtSize}
                      setTshirtSize={setTshirtSize}
                      tshirtSizes={tshirtSizes}
                      onBack={() => setActiveTab(0)}
                      onNext={() => setActiveTab(2)}
                    />
                  </Tab.Panel>

                  <Tab.Panel unmount={false}>
                    <TshirtAndPositionStep
                      mode="position"
                      position={position}
                      setPosition={setPosition}
                      positions={positions}
                      onBack={() => setActiveTab(1)}
                      onContinue={saveAndContinueToPayment}
                      saving={saving}
                      canContinue={canContinueToPayment}
                    />
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>
            </div>
          </DialogPanel>
        ) : (
          <DialogPanel className="w-full max-w-4xl bg-transparent shadow-none">
            <ModalIntelliPay
              athleteId={athleteId}
              athleteEmail={athleteEmail}
              eventId={eventId}
              origin={origin}
              onCloseAll={onClose}
              tShirtSize={tshirtSize}
              offerAcceptanceStatus={offerAcceptanceStatus}
              offerAcceptanceSchool={offerAcceptanceSchool}
              bundle={bundleChoice}
            />
          </DialogPanel>
        )}
      </div>
      <ToastContainer />
    </Dialog>
  );
}
